/**
 * A stone top's FABRICATION — what the fabricator bills beyond the top's
 * plan area, measured off the slab the 3D view draws (`Countertop.jsx`),
 * through the one set of rules both read (`cabinet-runs/countertopEnds.js`):
 *
 *   - waterfall legs are STONE, so their area joins the top's own
 *     (`waterfallLegAreaSqM`, read by `collectObjectFinishes`);
 *   - every exposed edge is polished, billed by the foot in the profile it
 *     is drawn with (`countertop-edge`);
 *   - a sink in the top needs a cutout, billed by the piece, an undermount's
 *     polished, a bathroom top's said so (`countertop-sink-cutout`; its facts
 *     also ride the sink's own take-off ref, `sinkCutoutsBySink`, for a
 *     price that supplies the bowl with the cut — `includedBowls.js`);
 *   - so does a cooktop set in it (`countertop-cooktop-cutout`) — though the
 *     3D view draws it sitting on an uncut slab (`seatCooktopsOnCounter`);
 *   - two tops that meet are joined at a seam, billed by the piece
 *     (`countertop-seam`).
 *
 * The last four reach the labor take-off as quantities per work item and
 * qualifier signature (`labor/signatureCounts.js`), the shape the electrical
 * devices use, so a profile and a polished cutout price on their own
 * variant lines. See reference-docs/estimates/overview.md, "Countertop
 * fabrication". Pure.
 */

const {
  isFreestandingCountertop,
} = require('../../../cabinet-runs/countertopExposure');
const {
  countertopEdgeProfile,
  countertopEndFinish,
  countertopSupportCabinet,
  waterfallEnds,
} = require('../../../cabinet-runs/countertopEnds');
const { countertopEdges } = require('../../../cabinet-runs/countertopEdges');
const { stoneBacksOf } = require('../../../cabinet-runs/stoneBack');
const { isSink, getSinkType } = require('../../../sink-types');
const { hostCountertopFor } = require('../../../cabinet-runs/reparent');
const { objectsAreTouching } = require('../../../geometry/objectsTouch');
const { primaryRoomKind } = require('../../../rooms/roomKind');
const {
  qualifierSignature,
  parseSignature,
} = require('../../../labor/qualifiers');
const {
  addSignatureCountsByRow,
  rowsKey,
  totalSignatureCounts,
} = require('../../../labor/signatureCounts');

const M_TO_FT = 3.28084;

// The profiles a fabricator charges apart from the plain polished edge.
// Eased and square are that plain edge; so is the legacy `waterfall` profile,
// which the 3D view draws as a square slab.
const PROFILED_EDGES = new Set(['beveled', 'bullnose', 'ogee']);

// Sinks whose hole is POLISHED, because its edge shows: an undermount, and
// an apron-front (farmhouse), which hangs below the slab the same way. A
// drop-in's rim hides a plain cut, and so does any sink that does not say.
const POLISHED_CUTOUT = new Set(['undermount', 'farmhouse']);
// Sinks that need no basin cutout: a vessel sits ON the stone (its drain
// hole is not a basin cutout), and the standalone mounts never stand in one.
const NO_CUTOUT = new Set(['vessel', 'pedestal', 'wall-mount']);

// The tops a seam can join. A tub deck stands apart from any counter.
const SEAMED_TYPES = new Set(['countertop', 'corner-fill-countertop']);

const isPolygonTop = (top) =>
  Array.isArray(top?.polygonCorners) && top.polygonCorners.length >= 3;

/**
 * A piece of countertop — a run or island slab, an outlined slab (an angled
 * end's), a corner piece. What its exposed edges are polished and profiled
 * on, and its sinks and cooktops cut into. A tub deck has a renderer and a
 * line of its own.
 */
const isCounterPiece = (top) =>
  top?.type === 'countertop' || top?.category?.countertop === true;

/**
 * A top the 3D view draws as a rectangular slab (`Countertop.jsx`), the only
 * shape it turns a waterfall leg down from. An outlined piece is extruded
 * from its outline.
 */
const drawnAsSlab = (top) => isCounterPiece(top) && !isPolygonTop(top);

/**
 * Where a top's waterfall reaches the floor: how many legs, at which ends
 * (`waterfallEnds` — an open end, never one on a peninsula's back line), and
 * the drop of each — the height of the cabinet it stands on. Null when it has
 * none: an end finish that is not a waterfall, no end to turn down at, or no
 * cabinet to drop past (the 3D view would draw a leg the slab's own
 * thickness there, which is not stone anyone cuts).
 */
function waterfallLegs(top, ctx) {
  if (!drawnAsSlab(top)) return null;
  if (countertopEndFinish(top, ctx) !== 'waterfall') return null;
  const dropM = countertopSupportCabinet(top, ctx.objects)?.dimensions?.y;
  if (!(dropM > 0)) return null;
  const ends = waterfallEnds(top, ctx);
  const legs = (ends.left ? 1 : 0) + (ends.right ? 1 : 0);
  return legs > 0 ? { legs, dropM, ends } : null;
}

/**
 * The stone in a top's waterfall legs, m²: each leg is the top's depth by the
 * height it drops. 0 for a top with none.
 *
 * @param {Object} top
 * @param {Object} ctx  `{ objects, cabinetRuns, walls }` — the room's objects
 *   and runs, and the walls that may cap a run end
 */
function waterfallLegAreaSqM(top, ctx) {
  const legs = waterfallLegs(top, ctx);
  const depth = top?.dimensions?.z;
  return legs && depth > 0 ? legs.legs * depth * legs.dropM : 0;
}

/**
 * The stone in the peninsula backs cut from a top's stone, m²: a back under
 * waterfall counters is one stone panel, cut from the counter over the most
 * of it (`stoneBacksOf`; user, 2026-10-29). Each back's length by its height;
 * 0 for a top no back is cut from. Billed with the top, in place of the wood
 * panel (`collectCabinetUnits` leaves that out).
 *
 * @param {Object} top
 * @param {Object} ctx  as `waterfallLegAreaSqM`
 */
function stoneBackAreaSqM(top, ctx) {
  return stoneBacksOf(top, ctx).reduce(
    (sum, back) => sum + back.dimensions.x * back.dimensions.y,
    0
  );
}

/**
 * All the stone a top's waterfall finish adds to its plan area, m²: its legs
 * and the peninsula backs cut from it — set on a line of their own
 * (`slab-countertop~waterfall`).
 */
const waterfallStoneAreaSqM = (top, ctx) =>
  waterfallLegAreaSqM(top, ctx) + stoneBackAreaSqM(top, ctx);

/**
 * The feet of polished edge a top is drawn with, by edge signature (`''` the
 * plain edge, `edge=ogee` a profile). Only the EXPOSED stretches of its edges
 * count (`countertopEdges`: not against a wall, another counter, or a tall
 * unit) — a seam, the stretch of a front another leg's slab starts against,
 * the back of a wall run are not polished:
 *
 *   - every exposed stretch of a long edge (and every edge of a corner piece)
 *     in the top's profile;
 *   - an exposed stretch of an END in the profile when the ends match it,
 *     plain when they are square;
 *   - for a waterfall, no end edge under a leg but the leg's open vertical
 *     edges, plain (the 3D view profiles only the top bar); an exposed end
 *     with no leg — one on a peninsula's back line — is a plain square end.
 */
function edgeFeet(top, ctx) {
  const profile = countertopEdgeProfile(top);
  const profiled = PROFILED_EDGES.has(profile)
    ? qualifierSignature({ edge: profile })
    : '';
  const finish = countertopEndFinish(top, ctx);
  const legs = waterfallLegs(top, ctx);
  const out = {};
  const add = (sig, metres) => {
    if (metres > 0) out[sig] = (out[sig] ?? 0) + metres * M_TO_FT;
  };
  // a leg stands at the slab's local -X (left) or +X (right) end
  const ry = top?.rotation?.y || 0;
  const localX = { x: Math.cos(ry), z: -Math.sin(ry) };
  const legUnder = (edge) => {
    if (!legs) return false;
    const along = edge.normal.x * localX.x + edge.normal.z * localX.z;
    return along > 0.5
      ? legs.ends.right
      : along < -0.5
        ? legs.ends.left
        : false;
  };
  for (const edge of countertopEdges(top, ctx)) {
    const exposed = edge.exposed.reduce((sum, [lo, hi]) => sum + hi - lo, 0);
    if (!edge.end) add(profiled, exposed);
    else if (legUnder(edge)) continue;
    else add(finish === 'match' ? profiled : '', exposed);
  }
  if (legs) {
    const sides = isFreestandingCountertop(top, ctx) ? 2 : 1;
    add('', legs.legs * legs.dropM * sides);
  }
  return Object.fromEntries(
    Object.entries(out).map(([sig, ft]) => [sig, Math.round(ft * 100) / 100])
  );
}

/** The sinks set in a top. */
const sinksIn = (top, objects) =>
  (objects || []).filter(
    (obj) =>
      isSink(obj) &&
      (obj.parents || []).some(
        (p) => p?.id === top.id && p?.type === 'countertop'
      )
  );

/**
 * The facts of a sink's cutout — an undermount's polished, a bathroom top's
 * (`bath`) said so — or null for a sink that needs none (a vessel sits on
 * the stone).
 */
function sinkCutoutFacts(sink, bath) {
  const mount = getSinkType(sink);
  if (NO_CUTOUT.has(mount)) return null;
  return {
    ...(POLISHED_CUTOUT.has(mount) ? { cutout: 'undermount' } : {}),
    ...(bath ? { top: 'bath' } : {}),
  };
}

/** The sink cutouts a top needs, by cutout signature. */
function sinkCutouts(top, objects, bath) {
  const out = {};
  for (const obj of sinksIn(top, objects)) {
    const facts = sinkCutoutFacts(obj, bath);
    if (!facts) continue;
    const sig = qualifierSignature(facts);
    out[sig] = (out[sig] ?? 0) + 1;
  }
  return out;
}

/** Is the room a bathroom, whose tops' cutouts are a bathroom top's? */
const isBathRoom = (room) => primaryRoomKind(room) === 'bathroom';

/**
 * Each sink cut into one of a room's stone tops — those
 * `countertopFabricationByRow` bills — by its object id: the facts its
 * cutout is billed with, the stone included.
 */
function sinkCutoutsBySink(
  tops,
  room,
  rowOf = () => null,
  stoneOf = () => null
) {
  const bath = isBathRoom(room);
  const out = new Map();
  for (const top of tops || []) {
    if (!isCounterPiece(top)) continue;
    for (const sink of sinksIn(top, room?.objects)) {
      const facts = sinkCutoutFacts(sink, bath);
      const stone = stoneOf(rowOf(top));
      if (facts) out.set(sink.id, stone ? { ...facts, stone } : facts);
    }
  }
  return out;
}

/**
 * The cooktop cutouts a top needs: one for each cooktop whose host is this
 * top. The host is found among ALL the room's counters by the rule that
 * seats a cooktop on its counter (`hostCountertopFor`), so a cooktop in a
 * tiled counter beside a stone one is not pinned to the stone. A range
 * stands between two tops and is cut into neither.
 */
function cooktopCutouts(top, objects) {
  const counters = (objects || []).filter((o) => o?.type === 'countertop');
  const count = (objects || []).filter(
    (o) =>
      o?.type === 'cooktop' && hostCountertopFor(o, counters)?.id === top.id
  ).length;
  return count > 0 ? { '': count } : {};
}

/** The pairs of these tops a seam joins: one for each pair that meets. */
function seamPairs(tops) {
  const seamed = tops.filter((t) => SEAMED_TYPES.has(t?.type));
  const pairs = [];
  for (let i = 0; i < seamed.length; i++) {
    for (let j = i + 1; j < seamed.length; j++) {
      if (objectsAreTouching(seamed[i], seamed[j]))
        pairs.push([seamed[i], seamed[j]]);
    }
  }
  return pairs;
}

/**
 * A room's countertop fabrication by the take-off row each top's stone is
 * billed on, `{ [rowsKey]: { [workItemId]: { [signature]: quantity } } }`
 * (`labor/signatureCounts`), so the labor is kept per item: a top's edges
 * and its cutouts under its own row, a seam under the rows of both tops
 * it joins — one row when they are one stone (2026-10-18).
 *
 * @param {Object[]} tops  the room's tops BILLED AS STONE — a slab finish,
 *   and none the scan found unchanged (an existing top is priced at nothing,
 *   its fabrication with it)
 * @param {Object} room    the room (its `objects` and `cabinetRuns`)
 * @param {Object[]} walls the walls that may cap a run end
 * @param {Function} [rowOf] a top → its row's key; none gives every count
 *   under `''`, the job's totals
 * @param {Function} [stoneOf] a row's key → its stone type (`stone`, e.g.
 *   'quartzite'), or null; every fee on a top cut in that stone signs it
 *   (design 6d). A seam takes it when any top it joins is cut in it — the
 *   seam is cut in that stone too.
 */
function countertopFabricationByRow(
  tops,
  room,
  walls,
  rowOf = () => null,
  stoneOf = () => null
) {
  const ctx = {
    objects: room?.objects || [],
    cabinetRuns: room?.cabinetRuns || [],
    walls: walls || [],
    bath: isBathRoom(room),
  };
  const byRow = {};
  // each signature with the stone the tops are cut in, in `AXES` order
  const withStone = (onTops, bySig) => {
    const stone =
      onTops.map((t) => stoneOf(rowOf(t))).find((s) => s != null) ?? null;
    if (!stone) return bySig;
    const out = {};
    for (const [sig, n] of Object.entries(bySig)) {
      const signed = qualifierSignature({ ...parseSignature(sig), stone });
      out[signed] = (out[signed] ?? 0) + n;
    }
    return out;
  };
  const put = (onTops, workItemId, bySig) => {
    if (Object.keys(bySig).length > 0) {
      addSignatureCountsByRow(byRow, {
        [rowsKey(onTops.map(rowOf))]: {
          [workItemId]: withStone(onTops, bySig),
        },
      });
    }
  };
  for (const top of tops || []) {
    if (!isCounterPiece(top)) continue;
    put([top], 'countertop-edge', edgeFeet(top, ctx));
    put(
      [top],
      'countertop-sink-cutout',
      sinkCutouts(top, ctx.objects, ctx.bath)
    );
    put([top], 'countertop-cooktop-cutout', cooktopCutouts(top, ctx.objects));
  }
  for (const pair of seamPairs(tops || []))
    put(pair, 'countertop-seam', { '': 1 });
  return byRow;
}

/**
 * A room's countertop fabrication, `{ [workItemId]: { [signature]: quantity } }`:
 * every top's, the job's totals (`countertopFabricationByRow`, summed).
 */
const countertopFabricationCounts = (tops, room, walls) =>
  totalSignatureCounts(countertopFabricationByRow(tops, room, walls));

module.exports = {
  PROFILED_EDGES,
  POLISHED_CUTOUT,
  waterfallLegs,
  waterfallLegAreaSqM,
  stoneBackAreaSqM,
  waterfallStoneAreaSqM,
  countertopFabricationByRow,
  countertopFabricationCounts,
  sinkCutoutsBySink,
};
